import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cellOf, dueLine } from "../../lib/portalKinds";
import { homeworkLine, hoursLook } from "../../lib/portalGroupTabs";
import { hoursText } from "../../lib/portalAttendance";
import AttendanceTable from "./AttendanceTable";
import { Chip } from "./PortalShell";
import Hint from "./Hint";
import GroupProfile from "../assessment/GroupProfile";
import { levelCell } from "../../lib/initialAssessmentAdmin";

const STATE_TONES = { below: "late", recovery: "warn", eliminated: "late" };

/**
 * One row per participant, everything the trainer follows about them
 * (06.10.2026): homework, asynchronous hours, live attendance. The details —
 * assignment by assignment, the hours line by line — fold under it.
 */
export default function ParticipantsTab({ roster, hours, presence, groupId, onOpen, fped = false, assessment = null,
  openProfile = false }) {
  const sheet = (eid) => `/portal/grupa/${groupId}/cursant/${eid}`;
  const actIds = roster.activities.map((a) => a.id);
  const hoursBy = Object.fromEntries((hours?.participants || []).map((p) => [p.enrollment_id, p]));
  const liveBy = Object.fromEntries((presence?.participants || []).map((p) => [p.enrollment_id, p]));
  const over = Boolean(presence?.over);
  // The initial assessment (15.10.2026): the accredited course, sent through the platform.
  const levels = assessment?.applies && !assessment.external
    ? Object.fromEntries((assessment.rows || []).map((r) => [r.enrollment_id, r])) : null;

  if (roster.participants.length === 0) {
    return (
      <p className="text-sm text-[#6B7280]">
        Niciun cursant în portal încă. Cursanții intră după ce își semnează contractul.
      </p>
    );
  }

  return (
    <>
      {levels && <ProfileFold assessment={assessment} open={openProfile} />}
      {/* Wide screens fit the table: no scroll box there, so a hint's popover is not clipped. */}
      <div className="overflow-x-auto rounded-2xl border border-[#E5E7EB] bg-white lg:overflow-visible">
        <table className="w-full min-w-[44rem] border-collapse text-sm" data-testid="portal-participants">
          <thead>
            <tr className="border-b border-[#E5E7EB] text-left text-xs text-[#6B7280]">
              <th scope="col" className="px-4 py-2.5 font-semibold">Cursant</th>
              <th scope="col" className="px-3 py-2.5 font-semibold">Teme</th>
              {/* No hours and no attendance on a Curs FPED cohort (12.10.2026). */}
              {!fped && (
                <th scope="col" className="px-3 py-2.5 font-semibold">
                  <span className="inline-flex items-center gap-1">Ore asincrone <Hint term="asyncHours" /></span>
                </th>
              )}
              {!fped && (
                <th scope="col" className="px-3 py-2.5 font-semibold">
                  <span className="inline-flex items-center gap-1">Prezență live <Hint term="liveAttendance" align="right" /></span>
                </th>
              )}
              {levels && <th scope="col" className="px-3 py-2.5 font-semibold">Nivel inițial (orientativ)</th>}
              <th scope="col" className="w-8 px-3 py-2.5"><span className="sr-only">Fișa</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {roster.participants.map((p) => {
              const hw = homeworkLine(p.cells, actIds);
              const h = hoursBy[p.enrollment_id];
              const look = hoursLook(h, { over, denominator: hours?.denominator });
              const live = liveBy[p.enrollment_id];
              return (
                <tr key={p.enrollment_id} className="align-top hover:bg-[#FAFAFB]">
                  <th scope="row" className="px-4 py-3 text-left font-semibold">
                    <Link to={sheet(p.enrollment_id)} className="text-[#373A45] hover:text-[#965EA7]">{p.name}</Link>
                    {p.read_only_until && <span className="ml-2 text-xs font-normal text-[#6B7280]">retras</span>}
                  </th>
                  <td className="px-3 py-3">
                    <p className="tabular-nums text-[#373A45]">
                      {hw.total ? `${hw.handed} din ${hw.total} predate` : "—"}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {hw.waiting > 0 && <Chip tone={hw.lateFeedback ? "late" : "info"}>{hw.waiting} așteaptă feedback</Chip>}
                      {hw.redo > 0 && <Chip tone="warn">{hw.redo} de refăcut</Chip>}
                      {hw.missing > 0 && <Chip tone="late">{hw.missing} {hw.missing === 1 ? "lipsește" : "lipsesc"}</Chip>}
                    </div>
                  </td>
                  {!fped && <td className="px-3 py-3">
                    {h ? (
                      <div className="w-40">
                        <div className="flex items-center justify-between text-xs tabular-nums text-[#373A45]">
                          <span>{hoursText(h.done_hours)} din {hoursText(hours.denominator)}</span>
                          {look.label && <Chip tone={look.tone}>{look.label}</Chip>}
                        </div>
                        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#E5E7EB]" aria-hidden="true">
                          <div className={`h-full rounded-full ${look.bar}`} style={{ width: `${Math.round(look.share * 100)}%` }} />
                        </div>
                      </div>
                    ) : "—"}
                  </td>}
                  {!fped && <td className="px-3 py-3 text-xs text-[#373A45]">
                    {live ? (
                      <>
                        <p className="tabular-nums">
                          teorie {live.components.theoretical.presence_percent}% · practică {live.components.practical.presence_percent}%
                        </p>
                        {live.state !== "ok" && (
                          <span className="mt-1 inline-block"><Chip tone={STATE_TONES[live.state] || "neutral"}>{live.state_label}</Chip></span>
                        )}
                      </>
                    ) : "—"}
                  </td>}
                  {levels && <LevelCell row={levels[p.enrollment_id]} />}
                  <td className="px-3 py-3 text-right">
                    <Link to={sheet(p.enrollment_id)} aria-label={`Fișa lui ${p.name}`} className="text-[#9CA3AF] hover:text-[#965EA7]">
                      <ChevronRight size={16} />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!fped && <p className="mt-2 text-xs text-[#6B7280]">
        {over
          ? "Cursul s-a încheiat: orele și prezența sunt cele finale. Pragul e de 20% absențe pe fiecare componentă."
          : "Cursul e în desfășurare: orele asincrone arată cât s-a făcut până acum, nu un verdict. Pragul se judecă la final."}
      </p>}

      {roster.activities.length > 0 && (
        <Fold title="Temă cu temă" hint="Starea fiecărei teme, pentru fiecare cursant">
          <div className="overflow-x-auto rounded-2xl border border-[#E5E7EB] bg-white">
            <table className="w-full min-w-[32rem] border-collapse text-sm" data-testid="portal-grid">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left">
                  <th scope="col" className="sticky left-0 bg-white px-4 py-3 text-xs font-semibold text-[#6B7280]">Cursant</th>
                  {roster.activities.map((a) => (
                    <th key={a.id} scope="col" className="px-3 py-3 align-bottom text-xs font-semibold text-[#373A45]">
                      <span className="block max-w-[11rem] truncate" title={a.title}>{a.title}</span>
                      <span className="block font-normal text-[#6B7280]">{dueLine(a.due_at)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {roster.participants.map((p) => (
                  <tr key={p.enrollment_id}>
                    <th scope="row" className="sticky left-0 bg-white px-4 py-2.5 text-left font-semibold">
                      <Link to={sheet(p.enrollment_id)} className="text-[#373A45] hover:text-[#965EA7]">{p.name}</Link>
                    </th>
                    {roster.activities.map((a) => {
                      const c = p.cells[a.id];
                      const cl = cellOf(c);
                      return (
                        <td key={a.id} className="px-3 py-2.5">
                          <Chip tone={c?.late_feedback ? "late" : cl.tone}>{cl.label}</Chip>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Fold>
      )}

      {hours && (
        <Fold title="Orele asincrone, în detaliu" hint="Ce lipsește fiecăruia · din ele iese Anexa 4.3">
          <AttendanceTable report={hours} settled={over} onOpen={onOpen} />
        </Fold>
      )}
    </>
  );
}

function LevelCell({ row }) {
  const cell = levelCell(row);
  return (
    <td className="px-3 py-3 text-sm">
      <span className={cell.missing ? "text-[#9CA3AF]" : "text-[#373A45]"}>{cell.text}</span>
      {cell.afterStart && <span className="block text-[11px] italic text-[#9CA3AF]">după începerea cursului</span>}
    </td>
  );
}

/** "Profilul grupei", above the table: closed, unless the agenda's line sent the trainer here. */
function ProfileFold({ assessment, open }) {
  const { completed, people } = assessment.counts || {};
  const ref = React.useRef(null);
  // After the app's scroll-to-top on a new address, and after the page brings the tabs into view.
  React.useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    return () => clearTimeout(t);
  }, [open]);
  return (
    <details ref={ref} open={open} className="group mb-4 scroll-mt-4 rounded-2xl border border-[#E5E7EB] bg-white"
      data-testid="portal-assessment-profile">
      <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
        <ChevronRight size={16} className="shrink-0 text-[#9CA3AF] transition-transform group-open:rotate-90" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-[#373A45]">Profilul grupei</span>
          <span className="block text-xs text-[#6B7280]">
            Din evaluarea inițială · {completed || 0} din {people || 0} cursanți au completat
          </span>
        </span>
      </summary>
      <div className="border-t border-[#E5E7EB] p-3">
        {assessment.profile?.answered
          ? <GroupProfile profile={assessment.profile} texts={assessment.texts || []} />
          : (
            <p className="text-sm text-[#6B7280]">
              Încă nu a completat nimeni evaluarea inițială. Cursanții o primesc pe email după ce semnează contractul.
            </p>
          )}
      </div>
    </details>
  );
}

function Fold({ title, hint, children }) {
  return (
    <details className="group mt-6 rounded-2xl border border-[#E5E7EB] bg-white">
      <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
        <ChevronRight size={16} className="shrink-0 text-[#9CA3AF] transition-transform group-open:rotate-90" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-[#373A45]">{title}</span>
          <span className="block text-xs text-[#6B7280]">{hint}</span>
        </span>
      </summary>
      <div className="border-t border-[#E5E7EB] p-3">{children}</div>
    </details>
  );
}
